// Snapshots of the ArcDisk demo backend, captured with ../../tools/snapshot.mjs
// after ONE real grading run per project (the scores are the model's actual
// output, not invented). The demo never calls the grading pipeline: it
// replays these fixtures, revealing each student's grades as the simulated
// assessment "finishes" them (see ../simulation.ts).
//
// Shapes are the ArcDisk API's own: GET /api/projects/dashboard,
// GET /api/grades, GET /api/projects/review, GET /api/projects/submissions
// and GET /api/disc/skills. Field names are snake_case on purpose — the
// copied components read them that way.
import projectsJson from './projects.json'
import studentsJson from './students.json'
import factory from './project__proj_pleasant_view_ms_2026_27_11_the_factory.json'
import wonders from './project__proj_pleasant_view_ms_2026_27_5_wonders_of_the_world_fair.json'
import plant from './project__proj_pleasant_view_ms_2026_27_6_plant_investigation_does_a_heat_lamp_help_plants_grow.json'
import s321 from './student__321.json'
import s327 from './student__327.json'
import s332 from './student__332.json'
import s333 from './student__333.json'
import s334 from './student__334.json'
import s335 from './student__335.json'

export interface DemoProjectListItem {
  project_id: string
  title: string | null
  grade: string | null
  academic_year: string | null
  submission_count: number
  file_count: number
  grade_count: number
}

export interface DashboardSkillRow {
  specific_skill_id: string
  specific_skill: string | null
  area: string | null
  area_id: string | null
  area_color: string
  competency: string | null
  competency_id: string | null
  skill: string | null
  skill_id: string | null
  class_avg: number | null
  n_graded: number
  n_mastered: number
  mastery_pct: number | null
  distribution: Record<string, number>
  skill_total_ss: number
}

export interface DashboardStudent {
  student_id: number
  student_name: string
  latest_submission_at: string | null
  attempts: number
  skills_graded: number
  overall_avg: number | null
  typical_level: number | null
  n_docs: number
  n_images: number
  has_failed: boolean
  has_unreadable: boolean
  completion: number | null
  completion_scored: number
  completion_total: number
}

export interface DashboardMatrixCell {
  student_id: number
  specific_skill_id: string
  score: number | null
}

export interface ProjectDashboard {
  meta: {
    project_id: string
    title: string | null
    grade: string | null
    subjects: string[]
    inference_status: string | null
    academic_year: string | null
    submissions_total: number
    submissions_graded: number
    submissions_ungraded: number
    students_submitted: number
    skills_total: number
    skills_assessed: number
  }
  stats: {
    class_avg: number | null
    class_mastery_pct: number | null
    students_needing_attention: number
    skills_struggling: number
    class_completion: number | null
  }
  // Area → Competency → Skill → synthetic mastery leaves (see derive.ts).
  curriculum: unknown[]
  scores: unknown[]
  skills: DashboardSkillRow[]
  students: DashboardStudent[]
  matrix: DashboardMatrixCell[]
}

export interface GradeRow {
  project_id: string
  submission_id: string
  student_id: number
  student_name: string | null
  attempt_no: number | null
  file_name: string | null
  subjects: string[] | null
  specific_skill_id: string
  specific_skill: string | null
  score_1_5: number | null
  evidence_snippets: unknown
  assessable: boolean
  rationale: string | null
  not_assessed_reason: string | null
  area: string | null
  competency: string | null
  skill: string | null
  created_at: string
}

export interface ProjectReview {
  standards: Array<Record<string, unknown> & { standard_path: string }>
  mappings: Array<Record<string, unknown> & { standard_path: string; specific_skill_id: string }>
}

export interface SubmissionAsset {
  id: number
  submission_id: number
  kind: 'document' | 'image' | 'video' | string
  status: string
  origin: string
  original_name: string | null
  mime: string | null
  size_bytes: number | null
  parent_asset_id: number | null
  page_index: number | null
  drive_file_id: string | null
  understanding: Record<string, unknown> | null
}

export interface SubmissionItem {
  id: number
  file_name: string | null
  mime: string | null
  drive_mime: string | null
  drive_file_id: string | null
  text_content: string | null
  created_at: string
  attempt_no: number | null
  stage: string | null
  subjects: string[] | null
  student_name: string | null
  assets: SubmissionAsset[]
}

export interface ProjectFixture {
  dashboard: ProjectDashboard
  grades: GradeRow[]
  review: ProjectReview
  submissions: Record<string, SubmissionItem[]>
}

export interface StudentDiscFixture {
  meta: {
    student_id: number
    student_name: string | null
    project_count: number
    submission_count: number
    graded_skills: number
    avg_score: number
  }
  scores: unknown[]
  curriculum: unknown[]
}

export interface DemoStudentListItem {
  student_id: number
  student_name: string
  grade_code: string | null
}

export const demoProjectList = projectsJson as DemoProjectListItem[]
export const demoStudentList = studentsJson as DemoStudentListItem[]

export const projectFixtures: Record<string, ProjectFixture> = {
  [factory.dashboard.meta.project_id]: factory as unknown as ProjectFixture,
  [wonders.dashboard.meta.project_id]: wonders as unknown as ProjectFixture,
  [plant.dashboard.meta.project_id]: plant as unknown as ProjectFixture,
}

export const studentFixtures: Record<number, StudentDiscFixture> = {
  321: s321 as unknown as StudentDiscFixture,
  327: s327 as unknown as StudentDiscFixture,
  332: s332 as unknown as StudentDiscFixture,
  333: s333 as unknown as StudentDiscFixture,
  334: s334 as unknown as StudentDiscFixture,
  335: s335 as unknown as StudentDiscFixture,
}
